import { useState, useEffect, useCallback } from "react";
import { api } from "@/lib/api-client";

export type OperatorTrendTier = "hot" | "cold";
export type OperatorTrendBucket = "day" | "week" | "month";

export interface OperatorTrendRow {
  day: string;
  docCount: number;
  avgKpm: number;
  correctionRate: number;
  avgTimePerFieldMs: number;
}

export interface OperatorTrendResponse {
  tier: OperatorTrendTier;
  bucket: OperatorTrendBucket;
  data: OperatorTrendRow[];
}

export function useOperatorTrend(
  operatorId: string | undefined,
  from?: string,
  to?: string,
  bucket?: OperatorTrendBucket,
) {
  const [data, setData] = useState<OperatorTrendResponse | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchData = useCallback(async () => {
    if (!operatorId) {
      setIsLoading(false);
      return;
    }
    setIsLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams();
      params.set("operatorId", operatorId);
      if (from) params.set("from", from);
      if (to) params.set("to", to);
      if (bucket) params.set("bucket", bucket);
      const query = `?${params.toString()}`;
      const result = await api.get<OperatorTrendResponse>(`/analytics/operator-trend${query}`);
      setData(result);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Failed to load operator trend");
    } finally {
      setIsLoading(false);
    }
  }, [operatorId, from, to, bucket]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  return { data, isLoading, error, refetch: fetchData };
}
